import type { FunctionalComponent } from 'vue'

import type { VxeGridProps } from '@vben/plugins/vxe-table'

import type { LightCheckboxSelectionOptions } from './light-checkbox-selection'
import type { RadioSelectionOptions } from './radio-selection'

import {
  setupVbenVxeTable as _setupVbenVxeTable,
  useVbenVxeGrid as _useVbenVxeGrid,
} from '@vben/plugins/vxe-table'

import { useVbenForm } from '../form'
import { registerCellRenderers } from './cell-renderers'
import { getVxeTableConfig } from './config'
import {
  createLightCheckboxSelection,
  resolveLightCheckboxSelection,
} from './light-checkbox-selection'
import { applyQuerySuccessEnhancement } from './query-success'
import { createRadioSelection, resolveRadioSelection } from './radio-selection'

// vxe-table adapter 的公共入口，只负责组装全局配置、渲染器和 opt-in 的选择能力。
// 具体能力拆到独立文件，避免入口文件继续膨胀。
interface UseVbenVxeGridOptions<T> extends VxeGridProps<T> {
  selection?: LightCheckboxSelectionOptions | RadioSelectionOptions<T>
}

export function setupVbenVxeTable() {
  _setupVbenVxeTable({
    configVxeTable: (vxeUI) => {
      vxeUI.setConfig(getVxeTableConfig())
      registerCellRenderers(vxeUI)
    },
    useVbenForm,
  })
}

function useVbenVxeGrid<T>(rawOptions: UseVbenVxeGridOptions<T>) {
  // 复制一层配置，后续注入 selection 能力时不直接修改调用方传入的原始对象。
  const options: VxeGridProps<T> = {
    ...rawOptions,
    gridEvents: {
      ...rawOptions.gridEvents,
    },
    gridOptions: {
      ...rawOptions.gridOptions,
    },
  }
  const selection = rawOptions.selection
  // selection 是扩展能力，只有业务显式传入对应配置时才会启用。
  const lightCheckboxSelection = resolveLightCheckboxSelection(selection)
  const radioSelection = resolveRadioSelection<T>(selection)

  let gridApiRef: any
  const lightCheckbox = lightCheckboxSelection
    ? createLightCheckboxSelection<T>({
        getDefaultKeyField: () =>
          options.gridOptions?.rowConfig?.keyField ?? 'id',
        getGridApi: () => gridApiRef,
        selection: lightCheckboxSelection,
      })
    : undefined

  const radio = radioSelection
    ? createRadioSelection<T>({
        getDefaultKeyField: () =>
          options.gridOptions?.rowConfig?.keyField ?? 'id',
        getGridApi: () => gridApiRef,
        selection: radioSelection,
      })
    : undefined

  if (lightCheckbox) {
    options.gridOptions = lightCheckbox.applyGridOptions(options.gridOptions)
  }

  if (radio) {
    options.gridOptions = radio.applyGridOptions(options.gridOptions)
    options.gridEvents = radio.applyGridEvents(options.gridEvents)
  }

  // 底层 vben grid 不认识 selection 字段，注入完成后要移除扩展配置。
  delete (options as UseVbenVxeGridOptions<T>).selection

  if (!options.gridClass) {
    options.gridClass = ''
  }
  options.gridClass += ' !p-0 '

  const [_Grid, gridApi] = _useVbenVxeGrid(options)
  gridApiRef = gridApi
  lightCheckbox?.patchGridApi(gridApi)

  // 仅真实配置查询函数的表格才注入 querySuccess。
  // 手动写入数据的表格没有 ajax.query，注入回调会被 VXE 误判为开启了 Proxy。
  if (typeof options.gridOptions?.proxyConfig?.ajax?.query === 'function') {
    applyQuerySuccessEnhancement({
      gridApi,
      onQuerySuccess(e) {
        radio?.restore(e.$grid)
      },
    })
  }

  const isGridMounted = ref(false)

  // 包一层组件，给业务侧保留表格挂载状态。
  const Grid: FunctionalComponent<VxeGridProps> = (props, { attrs, slots }) => {
    return (
      <_Grid
        onVnodeMounted={() => {
          isGridMounted.value = true
        }}
        {...props}
        {...attrs}
      >
        {slots}
      </_Grid>
    )
  }

  return [Grid, gridApi, { isGridMounted }] as const
}

export { useVbenVxeGrid }

export type { LightCheckboxSelectionOptions } from './light-checkbox-selection'
export type { RadioSelectionOptions } from './radio-selection'
export type * from '@vben/plugins/vxe-table'
